// MSAL.js auth path — used when the app runs outside Office (a regular
// browser tab on https://slides.trackyour.app). Office mode uses
// OfficeRuntime.auth.getAccessToken instead; see api/client.ts for the branch.

import {
  PublicClientApplication,
  type AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';

const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID;
// When the AAD app is single-tenant (`signInAudience: AzureADMyOrg`), MSAL must
// use a tenant-specific authority — the `/common` endpoint is reserved for
// multi-tenant apps and AAD rejects it with AADSTS50194. If VITE_AAD_TENANT_ID
// is set, use it; otherwise fall back to `common` for multi-tenant deploys.
const TENANT_ID = (import.meta.env.VITE_AAD_TENANT_ID || 'common') as string;

let _instance: PublicClientApplication | null = null;
let _initPromise: Promise<void> | null = null;

function getInstance(): PublicClientApplication {
  if (_instance) return _instance;
  if (!CLIENT_ID) {
    throw new Error('VITE_AAD_CLIENT_ID is required for web-mode auth');
  }
  _instance = new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
  return _instance;
}

async function ensureInit(): Promise<void> {
  if (_initPromise) return _initPromise;
  const inst = getInstance();
  _initPromise = (async () => {
    await inst.initialize();
    // If we just returned from a redirect-based sign-in, this picks up the
    // response. Popup flow is the default below, so this is a safety net.
    await inst.handleRedirectPromise();
  })();
  return _initPromise;
}

// The audience our backend expects matches the AAD app's `Application ID URI`:
// `api://<frontend-host>/<client-id>`. The `/access_as_user` scope name is the
// one we exposed in Step 0.4 of the runbook.
function apiScope(): string {
  return `api://${window.location.hostname}/${CLIENT_ID}/access_as_user`;
}

export async function getAccount(): Promise<AccountInfo | null> {
  await ensureInit();
  const accounts = getInstance().getAllAccounts();
  return accounts[0] ?? null;
}

export async function signIn(): Promise<AccountInfo> {
  await ensureInit();
  const result = await getInstance().loginPopup({
    scopes: [apiScope()],
    prompt: 'select_account',
  });
  return result.account!;
}

export async function signOut(): Promise<void> {
  await ensureInit();
  const account = getInstance().getAllAccounts()[0];
  if (!account) return;
  await getInstance().logoutPopup({ account });
}

export async function getAccessToken(): Promise<string> {
  await ensureInit();
  const inst = getInstance();
  const account = inst.getAllAccounts()[0];
  if (!account) throw new Error('Not signed in');

  try {
    const result = await inst.acquireTokenSilent({ scopes: [apiScope()], account });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      const result = await inst.acquireTokenPopup({ scopes: [apiScope()], account });
      return result.accessToken;
    }
    throw e;
  }
}
